import EmblaCarousel from './embla-carousel.js';

const AUTOPLAY_INTERVAL_FALLBACK = 5;

class ProductGallery extends HTMLElement {
  connectedCallback() {
    const viewport = this.querySelector('[data-product-gallery-viewport]');
    const track = this.querySelector('[data-product-gallery-track]');

    if (!viewport || !track || track.children.length === 0) return;

    this._track = track;
    this._deferredLoaded = false;

    // This gallery can sit nested inside a parent carousel (e.g.
    // clp-product-row's collection-carousel). Embla binds its drag-start
    // listeners directly on each carousel's own viewport element, and
    // touchstart/mousedown bubble — so without stopping them here, swiping
    // a card's own image gallery also bubbles up and drags the parent row.
    const stopNestedCarouselDrag = (event) => event.stopPropagation();
    viewport.addEventListener('touchstart', stopNestedCarouselDrag);
    viewport.addEventListener('mousedown', stopNestedCarouselDrag);

    // Snap per slide in every view (column, grid and row) so the progress bar
    // reads as a tab strip: the track is split into one segment per snap
    // position and the segment advances a whole step per image, never
    // drifting continuously with the drag.
    this._emblaApi = EmblaCarousel(viewport, {
      loop: this.dataset.loop === 'true',
      align: 'start',
      containScroll: 'trimSnaps',
    });

    const progressThumb = this.querySelector('[data-product-gallery-progress-thumb]');
    if (progressThumb) {
      // Custom properties only — base.css derives the thumb's geometry from them.
      const syncProgress = () => {
        progressThumb.style.setProperty('--gallery-progress-steps', this._emblaApi.scrollSnapList().length);
        progressThumb.style.setProperty('--gallery-progress-index', this._emblaApi.selectedScrollSnap());
      };

      this._emblaApi.on('init', syncProgress);
      this._emblaApi.on('reInit', syncProgress);
      this._emblaApi.on('select', syncProgress);
    }

    this._loadDeferredImages = () => {
      if (this._deferredLoaded) return;
      this._deferredLoaded = true;

      for (const image of this._track.querySelectorAll('img[data-src]')) {
        image.src = image.dataset.src;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        delete image.dataset.src;
        delete image.dataset.srcset;
      }
    };

    this._emblaApi.on('pointerDown', this._loadDeferredImages);
    this._emblaApi.on('select', this._loadDeferredImages);
    this._emblaApi.on('pointerDown', () => clearInterval(this._autoplayTimer));

    const autoplay = this.dataset.autoplay === 'true';
    const autoplayInterval = Number(this.dataset.autoplayInterval) || AUTOPLAY_INTERVAL_FALLBACK;

    if (autoplay && this._emblaApi.slideNodes().length > 1) {
      this._autoplayTimer = setInterval(() => this._emblaApi.scrollNext(), autoplayInterval * 1000);
    }

    for (const slide of this._track.querySelectorAll('[data-gallery-media-id]')) {
      slide.addEventListener('click', (event) => {
        const { galleryMediaId } = event.currentTarget.dataset;
        event.currentTarget.dispatchEvent(
          new CustomEvent('gallery:open-viewer', { bubbles: true, detail: { mediaId: galleryMediaId } })
        );
      });
    }
  }

  // Matched by media id rather than image URL — slide URLs are rendered at a
  // different width (and protocol form) than any variant image URL, so a URL
  // comparison can never match. Galleries whose slides carry no media id
  // (product cards) are left untouched.
  setActiveMedia(mediaId) {
    if (!this._emblaApi || !mediaId) return;

    const slides = [...this._track.children];
    const matchIndex = slides.findIndex((slide) => slide.dataset.galleryMediaId === String(mediaId));
    if (matchIndex === -1) return;

    this._loadDeferredImages?.();
    this._emblaApi.scrollTo(matchIndex);
  }

  disconnectedCallback() {
    this._emblaApi?.destroy();
    clearInterval(this._autoplayTimer);
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
